/**
 * HomePageForAdmin Component
 * 관리자 전용 홈 페이지 (모든 협력사 및 라이더 정보 조회)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../core/components/BottomNavigation';
import CustomSelect, { SelectOption } from '../../../core/components/CustomSelect';
import { getSupplierList, getRiderList, getRidersBySupplier, switchToSupplier, switchToRider, SupplierListItem, RiderListItem } from '../api/adminApi';
import { setCookie } from '../../../core/utils/cookie';
import styles from '../styles/HomePageForSupplier.module.css';

type ViewMode = 'supplier' | 'rider';

const HomePageForAdmin: React.FC = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState<SupplierListItem[]>([]);
    const [riders, setRiders] = useState<RiderListItem[]>([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
    const [selectedRiderId, setSelectedRiderId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                setIsLoading(true);
                const data = await getSupplierList();
                setSuppliers(data);
            } catch (err: any) {
                console.error('협력사 목록 조회 실패:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSuppliers();
    }, []);

    useEffect(() => {
        const fetchRiders = async () => {
            try {
                setIsLoading(true);
                const data = await getRiderList();
                setRiders(data);
            } catch (err: any) {
                console.error('라이더 목록 조회 실패:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRiders();
    }, []);

    const supplierOptions: SelectOption[] = suppliers.map(s => ({
        value: s.suppId,
        label: s.suppName
    }));

    const riderOptions: SelectOption[] = riders.map(r => ({
        value: r.riderId,
        label: r.riderName
    }));

    const handleSupplierChange = (value: string) => {
        setSelectedSupplierId(value);
        setSelectedRiderId('');
        
        if (value) {
            fetchRidersBySupplier(value);
        } else {
            fetchAllRiders();
        }
    };

    const fetchRidersBySupplier = async (suppId: string) => {
        try {
            setIsLoading(true);
            const data = await getRidersBySupplier(suppId);
            setRiders(data);
        } catch (err: any) {
            console.error('협력사 라이더 목록 조회 실패:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllRiders = async () => {
        try {
            setIsLoading(true);
            const data = await getRiderList();
            setRiders(data);
        } catch (err: any) {
            console.error('라이더 목록 조회 실패:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRiderChange = (value: string) => {
        setSelectedRiderId(value);
    };

    const handleConfirm = async () => {
        try {
            setIsLoading(true);
            
            if (selectedSupplierId) {
                // 협력사 전환: 새 토큰 발급
                const response = await switchToSupplier(selectedSupplierId);
                
                // 토큰 저장
                setCookie('accessToken', response.accessToken);
                setCookie('refreshToken', response.refreshToken);
                
                // localStorage 업데이트
                const userInfo = {
                    suppId: selectedSupplierId,
                    prmCode: response.prmCode
                };
                localStorage.setItem('userInfo', JSON.stringify(userInfo));
                
                navigate('/supplierHome');
                
            } else if (selectedRiderId) {
                // 라이더 전환: 새 토큰 발급
                const response = await switchToRider(selectedRiderId);
                
                // 토큰 저장
                setCookie('accessToken', response.accessToken);
                setCookie('refreshToken', response.refreshToken);
                
                // localStorage 업데이트
                const userInfo = {
                    riderId: selectedRiderId,
                    prmCode: response.prmCode
                };
                localStorage.setItem('userInfo', JSON.stringify(userInfo));
                
                navigate('/home');
            }
        } catch (err: any) {
            console.error('전환 실패:', err);
            alert('전환 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const goToMyPage = () => {
        navigate('/my-page');
    };

    return (
        <div className={`screen active ${styles['supplier-home-screen']}`}>
            {/* 헤더 */}
            <div className={styles['supplier-header']}>
                <div className={styles['supplier-header-top']}>
                    <div className={styles['supplier-title-section']}>
                        <h1 className={styles['supplier-title']}>Vroong</h1>
                        <div className={styles['supplier-info']}>
                            <span className={styles['supplier-name']}>관리자</span>
                        </div>
                    </div>
                    <div className={styles['supplier-header-actions']}>
                        <button className={styles['settings-btn']} onClick={goToMyPage}>
                            <i className="fas fa-cog"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['supplier-container']}>
                <div className={styles['section']}>
                    <h2 className={styles['section-title']}>관리자 대시보드</h2>
                    <div className={styles['section-content']}>
                        {isLoading ? (
                            <p style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                                로딩 중...
                            </p>
                        ) : (
                            <>
                                {/* 협력사 선택 */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        fontSize: '14px', 
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        협력사 선택
                                    </label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <CustomSelect
                                            options={supplierOptions}
                                            value={selectedSupplierId}
                                            onChange={handleSupplierChange}
                                            placeholder="선택 및 협력사명 검색"
                                            searchable={true}
                                            className={styles['custom-select-flex']}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="협력사 ID 입력"
                                            value={selectedSupplierId}
                                            onChange={(e) => handleSupplierChange(e.target.value)}
                                            style={{ flex: '0 0 200px' }}
                                        />
                                    </div>
                                </div>

                                {/* 라이더 선택 */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        fontSize: '14px', 
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        라이더 선택
                                    </label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <CustomSelect
                                            options={riderOptions}
                                            value={selectedRiderId}
                                            onChange={handleRiderChange}
                                            placeholder="선택 및 이름 검색"
                                            searchable={true}
                                            className={styles['custom-select-flex']}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="라이더 ID 입력"
                                            value={selectedRiderId}
                                            onChange={(e) => handleRiderChange(e.target.value)}
                                            style={{ flex: '0 0 200px' }}
                                        />
                                    </div>
                                </div>

                                <button 
                                    className="btn btn-primary btn-gradient"
                                    onClick={handleConfirm}
                                    disabled={!selectedSupplierId && !selectedRiderId}
                                    style={{ width: '100%' }}
                                >
                                    확인
                                </button>
                                <p style={{ marginTop: '10px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                                    협력사 또는 라이더를 선택하고 확인을 누르세요
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="home" />
        </div>
    );
};

export default HomePageForAdmin;
